import { useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { FormProvider, useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, CircularProgress, Stack, Typography } from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import "dayjs/locale/ru"
import { toast } from "react-toastify"
import { ActionPlanStepCard } from "./ActionPlanStepCard"
import { TActionPlanFormValues, actionPlanSchema } from "../ZodValidationSchema/ActionPlanSchema"
import {
    useCreateProjectStepMutation,
    useDeleteProjectStepMutation,
    useGetProjectStepsQuery,
    useUpdateProjectStepMutation,
} from "../../../../../api"
import {
    areActionPlanFormValuesEqual,
    areActionPlanItemsEqual,
    mapFormItemToCreateBody,
    mapFormItemToUpdateBody,
    mapStepResourceToFormItem,
} from "./helpers/projectStepFormMappers"

export const EditActionPlanForm = () => {
    const { id } = useParams<{ id: string }>()
    const projectId = Number(id)
    const [draftIndex, setDraftIndex] = useState<number | null>(null)
    const [savingDraftIndex, setSavingDraftIndex] = useState<number | null>(null)
    const [isSavingPlan, setIsSavingPlan] = useState(false)
    const baselineRef = useRef<TActionPlanFormValues | null>(null)
    const [createProjectStep, { isLoading: isCreateStepLoading }] = useCreateProjectStepMutation()
    const [deleteProjectStep, { isLoading: isDeleteStepLoading }] = useDeleteProjectStepMutation()
    const [updateProjectStep] = useUpdateProjectStepMutation()

    const methods = useForm<TActionPlanFormValues>({
        mode: 'onBlur',
        resolver: zodResolver(actionPlanSchema),
        defaultValues: {
            actionPlan: [],
        },
    })

    const { control, reset, trigger, getValues } = methods
    const { fields, append, remove } = useFieldArray({
        control,
        name: "actionPlan",
    })

    const { data: projectSteps, isLoading, isError } = useGetProjectStepsQuery({ slugOrId: id ?? '' })

    useEffect(() => {
        if (!projectSteps) {
            return
        }

        const nextValues: TActionPlanFormValues = {
            actionPlan: projectSteps.map(mapStepResourceToFormItem),
        }
        reset(nextValues)
        baselineRef.current = nextValues
        setDraftIndex(null)
    }, [projectSteps, reset])

    if (isLoading) {
        return (
            <Stack alignItems="center" justifyContent="center" height="100%">
                <CircularProgress />
            </Stack>
        )
    }

    if (isError) {
        return (
            <Typography color="error" textAlign="center" px={2}>
                Не удалось загрузить шаги плана действий
            </Typography>
        )
    }

    const addItem = () => {
        if (draftIndex !== null || savingDraftIndex !== null || isSavingPlan) {
            return
        }
        append({
            title: "",
            date_from: "",
            date_to: "",
            description: "",
            responsibles: [],
        })
        setDraftIndex(fields.length)
    }

    const removeItem = async (index: number) => {
        if (draftIndex !== null && index !== draftIndex) {
            toast.info("Сначала сохраните или удалите новый шаг")
            return
        }
        if (savingDraftIndex !== null || isDeleteStepLoading || isCreateStepLoading || isSavingPlan) {
            return
        }

        if (draftIndex === index) {
            remove(index)
            setDraftIndex(null)
            return
        }

        if (Number.isNaN(projectId)) {
            return
        }
        const stepId = getValues(`actionPlan.${index}.stepId`)
        if (!stepId) {
            toast.error("Не удалось определить шаг для удаления")
            return
        }

        try {
            await deleteProjectStep({ projectId, stepId }).unwrap()
            remove(index)
            toast.success("Шаг плана удалён")
        } catch {
            toast.error("Не удалось удалить шаг плана")
        }
    }

    const saveDraftStep = async (index: number) => {
        if (draftIndex !== index) return
        if (Number.isNaN(projectId)) {
            toast.error("Не удалось определить проект")
            return
        }

        const isValid = await trigger(`actionPlan.${index}`)
        if (!isValid) {
            toast.info("Проверь поля шага: есть ошибки валидации")
            return
        }

        const item = getValues(`actionPlan.${index}`)
        const body = mapFormItemToCreateBody(item)

        setSavingDraftIndex(index)
        try {
            await createProjectStep({ projectId, body }).unwrap()
            toast.success("Шаг плана сохранён")
            setDraftIndex(null)
        } catch {
            toast.error("Не удалось сохранить шаг плана")
        } finally {
            setSavingDraftIndex(null)
        }
    }

    const saveActionPlan = async () => {
        if (draftIndex !== null) {
            toast.info("Сначала сохраните или удалите новый шаг")
            return
        }
        if (Number.isNaN(projectId)) {
            toast.error("Не удалось определить проект")
            return
        }

        const isValid = await trigger("actionPlan")
        if (!isValid) {
            toast.info("Проверь поля шагов: есть ошибки валидации")
            return
        }

        const current = getValues()
        const baseline = baselineRef.current
        if (baseline && areActionPlanFormValuesEqual(current, baseline)) {
            toast.info("Нет изменений")
            return
        }

        const updates = current.actionPlan
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => item.stepId != null)
            .filter(({ item, index }) => {
                const baseItem = baseline?.actionPlan[index]
                if (!baseItem) return true
                return !areActionPlanItemsEqual(item, baseItem)
            })

        if (updates.length === 0) {
            toast.info("Нет изменений")
            return
        }

        setIsSavingPlan(true)
        try {
            for (const { item } of updates) {
                await updateProjectStep({
                    projectId,
                    stepId: item.stepId!,
                    body: mapFormItemToUpdateBody(item),
                }).unwrap()
            }
            toast.success(
                updates.length === 1 ? "Шаг плана обновлён" : "Шаги плана обновлены",
            )
            baselineRef.current = current
        } catch {
            toast.error("Не удалось сохранить изменения плана")
        } finally {
            setIsSavingPlan(false)
        }
    }

    const isBusy =
        isCreateStepLoading ||
        isDeleteStepLoading ||
        savingDraftIndex !== null ||
        isSavingPlan

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
            <FormProvider {...methods}>
                <Stack gap={2} height="100%">
                    {fields.length === 0 ? (
                        <>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    type="button"
                                    variant="action"
                                    onClick={addItem}
                                    disabled={draftIndex !== null || isBusy}
                                >
                                    Добавить
                                </Button>
                            </Stack>
                            <Stack
                                alignItems="center"
                                justifyContent="center"
                                sx={{ minHeight: '60vh' }}
                            >
                                <Typography variant="black-16-semiBold">
                                    План действий пуст
                                </Typography>
                            </Stack>
                        </>
                    ) : (
                        fields.map((field, index) => (
                            <ActionPlanStepCard
                                key={field.id}
                                index={index}
                                isDraft={draftIndex === index}
                                isSavingDraft={savingDraftIndex === index}
                                onRemove={() => { void removeItem(index) }}
                                onSaveDraft={() => { void saveDraftStep(index) }}
                            />
                        ))
                    )}
                    {fields.length > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Button
                                type="button"
                                variant="action"
                                onClick={addItem}
                                disabled={draftIndex !== null || isBusy}
                            >
                                Добавить
                            </Button>
                            <Button
                                type="button"
                                variant="follow"
                                disabled={isBusy || draftIndex !== null}
                                onClick={() => { void saveActionPlan() }}
                            >
                                {isSavingPlan ? (
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <CircularProgress size={16} color="inherit" />
                                        <span>Сохранение...</span>
                                    </Stack>
                                ) : (
                                    "Сохранить"
                                )}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </FormProvider>
        </LocalizationProvider>
    )
}
